import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "@/hooks/auth/use-auth";
import { useOrganizationSessionStorage } from "@/hooks/use-browser-storage";
import { getOrgScopedItem, removeOrgScopedItem } from "@/lib/saved-view-utils";

interface SavedViewContextValue {
  loadedViewId: string | null;
  hasUnsavedChanges: boolean;
  setLoadedView: (viewId: string | null) => void;
  isLoadingView: boolean;
  setIsLoadingView: (loading: boolean, preserveChangesFlag?: boolean) => void;
  markAsChanged: () => void;
  clearChanges: () => void;
}

const SavedViewContext = createContext<SavedViewContextValue | undefined>(
  undefined,
);

interface SavedViewProviderProps {
  children: ReactNode;
  pageType: string;
}

export function SavedViewProvider({
  children,
  pageType,
}: SavedViewProviderProps) {
  const { user } = useAuth();
  const organizationId = user?.company?.default_organization?.id || "";

  // Track which view is currently loaded - use sessionStorage for persistence
  const [loadedViewId, setLoadedViewIdState] = useOrganizationSessionStorage<
    string | null
  >(
    pageType
      ? `saved-views::${pageType}::loaded-view-id`
      : "saved-views::INVALID::loaded-view-id",
    null,
  );
  const [isLoadingViewState, setIsLoadingViewState] = useState(false);

  // Keep in sessionStorage for tab persistence
  const [hasUnsavedChanges, setHasUnsavedChanges] =
    useOrganizationSessionStorage<boolean>(
      pageType
        ? `saved-views::${pageType}::has-changes`
        : "saved-views::INVALID::has-changes",
      false,
    );

  // Track when we're ready to detect changes
  const isReadyForChangesRef = useRef(false);
  const mountTimeoutRef = useRef<number>();

  // Setup on mount - DO NOT clear hasUnsavedChanges to preserve unsaved changes
  useEffect(() => {
    // Don't clear hasUnsavedChanges - preserve it across navigation/refresh
    isReadyForChangesRef.current = false;
    window.__isLoadingView = false; // Ensure clean state

    // Check if this is a one-time share restore
    if (organizationId) {
      const oneTimeRestore = getOrgScopedItem(
        organizationId,
        "sessionStorage",
        "saved-views::one-time-restore",
      );

      if (oneTimeRestore) {
        // This is a one-time share - set hasChanges and clear the flag
        setHasUnsavedChanges(true);
        setLoadedViewIdState(null); // System default
        removeOrgScopedItem(
          organizationId,
          "sessionStorage",
          "saved-views::one-time-restore",
        );
      }
    }

    // Wait for initial setup to complete
    mountTimeoutRef.current = window.setTimeout(() => {
      if (!window.__isLoadingView) {
        isReadyForChangesRef.current = true;
      }
    }, 500);

    return () => {
      if (mountTimeoutRef.current) {
        window.clearTimeout(mountTimeoutRef.current);
      }
      isReadyForChangesRef.current = false;
    };
  }, [organizationId, setHasUnsavedChanges, setLoadedViewIdState]);

  // Update loading state - handles view loads at any time
  const setIsLoadingView = useCallback(
    (loading: boolean, preserveChangesFlag: boolean = false) => {
      window.__isLoadingView = loading;
      setIsLoadingViewState(loading);

      if (loading) {
        // Starting to load - optionally clear changes and block tracking
        if (!preserveChangesFlag) {
          setHasUnsavedChanges(false);
        }
        isReadyForChangesRef.current = false;

        // Cancel mount timeout if we're loading during mount
        if (mountTimeoutRef.current) {
          window.clearTimeout(mountTimeoutRef.current);
          mountTimeoutRef.current = undefined;
        }
      } else {
        // Done loading - enable tracking after components update
        window.setTimeout(() => {
          isReadyForChangesRef.current = true;
          window.__isLoadingView = false; // Extra safety
        }, 100);
      }
    },
    [setHasUnsavedChanges],
  );

  // Listen for configuration changes
  useEffect(() => {
    if (!organizationId || !pageType) return;

    /**
     * Checks if a storage key is a "shared" key at ANY ancestor scope level.
     *
     * A shared key's prefix must be a STRICT prefix of the pageType,
     * meaning the key lives "above" the current page in the hierarchy.
     *
     * For pageType "reports::analytics::summary":
     *   "reports::shared-key"              ✓ grandparent level
     *   "reports::analytics::shared-key"   ✓ parent level
     *   "reports::analytics::summary::key" ✗ direct page key (separate check)
     *   "reports::other::shared-key"       ✗ cousin branch (excluded)
     */
    const isAncestorSharedKey = (keyWithoutOrg: string): boolean => {
      const keySegments = keyWithoutOrg.split("::");
      const pageSegments = pageType.split("::");

      // Key prefix is all segments except the last (the actual key name)
      const keyPrefixLength = keySegments.length - 1;

      // Must have at least one prefix segment
      if (keyPrefixLength < 1) return false;
      // Must be strictly shorter than pageType (ancestor, not same level or deeper)
      if (keyPrefixLength >= pageSegments.length) return false;

      // Check that key prefix matches the corresponding pageType segments
      for (let i = 0; i < keyPrefixLength; i++) {
        if (keySegments[i] !== pageSegments[i]) return false;
      }

      return true;
    };

    // Helper to check if a key belongs to this page scope (including shared ancestor keys)
    const isPageScopedKey = (key: string): boolean => {
      // Check if it's for this page scope (e.g., "campaigns::list::")
      if (key.includes(`${pageType}::`)) return true;

      // Extract the key part after the org prefix for ancestor check
      const orgPrefix = `${organizationId}::`;
      const keyWithoutOrg = key.startsWith(orgPrefix)
        ? key.substring(orgPrefix.length)
        : key;

      // Check if it's a shared key at an ancestor level
      return isAncestorSharedKey(keyWithoutOrg);
    };

    const handleConfigChange = (event: Event) => {
      const customEvent = event as CustomEvent;

      // Check readiness first
      if (!isReadyForChangesRef.current || window.__isLoadingView) {
        return;
      }

      // Only track changes for keys belonging to this page (including shared keys)
      if (isPageScopedKey(customEvent.detail?.key || "")) {
        // Ignore certain keys that shouldn't trigger changes
        const ignorePatterns = [
          "has-changes",
          "last-applied",
          "saved-views",
          // Selection keys should not count as configuration changes
          "selected-campaign::active",
          "selected-campaign::recent",
          "selected-channel::active",
          "selected-channel::recent",
          "selected-webpage::active",
          "selected-webpage::recent",
          "selected-salesperson::active",
          "selected-salesperson::recent",
          "selected-account::active",
          "selected-account::recent",
        ];
        const shouldIgnore = ignorePatterns.some((pattern) =>
          customEvent.detail.key.includes(pattern),
        );

        if (!shouldIgnore) {
          setHasUnsavedChanges(true);
        }
      }
    };

    window.addEventListener("viewConfigChanged", handleConfigChange);

    return () => {
      window.removeEventListener("viewConfigChanged", handleConfigChange);
    };
  }, [organizationId, pageType, setHasUnsavedChanges]);

  // Manual methods to control changes
  const markAsChanged = useCallback(() => {
    if (isReadyForChangesRef.current && !window.__isLoadingView) {
      setHasUnsavedChanges(true);
    }
  }, [setHasUnsavedChanges]);

  const clearChanges = useCallback(() => {
    setHasUnsavedChanges(false);
  }, [setHasUnsavedChanges]);

  const setLoadedView = useCallback(
    (viewId: string | null) => {
      setLoadedViewIdState(viewId);
    },
    [setLoadedViewIdState],
  );

  const value: SavedViewContextValue = {
    loadedViewId,
    hasUnsavedChanges,
    setLoadedView,
    isLoadingView: isLoadingViewState,
    setIsLoadingView,
    markAsChanged,
    clearChanges,
  };

  return (
    <SavedViewContext.Provider value={value}>
      {children}
    </SavedViewContext.Provider>
  );
}

export function useSavedViewContext() {
  const context = useContext(SavedViewContext);
  if (!context) {
    throw new Error(
      "useSavedViewContext must be used within SavedViewProvider",
    );
  }
  return context;
}

// Optional version that returns null if not in context
export function useSavedViewContextOptional() {
  return useContext(SavedViewContext);
}
